import React, { useCallback, useEffect, useState } from 'react';
/**
 * MultipleChoiceRenderer — Option selection with keyboard shortcuts.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s66.
 */

import { motion } from 'framer-motion';
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { shuffleOptions } from '../../../utils/deterministicShuffle';
export function MultipleChoiceRenderer({
  question,
  onAnswer,
  onReadyChange
}: QuestionRendererProps) {
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [shuffledOptions, setShuffledOptions] = useState<Array<{
    text: string;
    originalIndex: number;
  }>>([]);
  useEffect(() => {
    setShuffledOptions(shuffleOptions(question.options, question.id));
    setSelectedOption(null);
  }, [question.id, question.options]);
  useEffect(() => {
    onReadyChange?.(selectedOption !== null);
  }, [selectedOption, onReadyChange]);
  const handleSelect = useCallback((index: number) => {
    setSelectedOption(index);
    if (shuffledOptions[index]) {
      onAnswer(shuffledOptions[index].originalIndex);
    }
  }, [shuffledOptions, onAnswer]);
  // Keyboard: 1-9 keys for quick selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = parseInt(e.key);
      if (!isNaN(key) && key >= 1 && key <= 9) {
        const index = key - 1;
        if (index < shuffledOptions.length) {
          handleSelect(index);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shuffledOptions, handleSelect]);
  return <div className="grid grid-cols-1 gap-4" data-id="element-7129">
      {shuffledOptions.map((option, idx) => <motion.button key={option.originalIndex} initial={{
      opacity: 0,
      x: -20
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.3 + idx * 0.1
    }} whileHover={{
      scale: 1.01,
      x: 4
    }} whileTap={{
      scale: 0.99
    }} onClick={() => handleSelect(idx)} className={`
            p-5 rounded-2xl text-left text-lg font-medium transition-all duration-200 border-2 min-h-[72px] flex items-center group relative
            ${selectedOption === idx ? 'border-indigo-600 bg-indigo-50 text-indigo-900 shadow-md ring-2 ring-indigo-200 ring-offset-2' : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-slate-50 hover:shadow-sm'}
          `} data-id="element-7130">
          <span className={`
              inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold mr-4 transition-colors
              ${selectedOption === idx ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-indigo-100 group-hover:text-indigo-600'}
            `} data-id="element-7131">
            {String.fromCharCode(65 + idx)}
          </span>
          <span className="flex-1" data-id="element-7132">
            <LatexRenderer content={option.text} data-id="element-7133" />
          </span>
          {idx < 9 && <span className="absolute right-4 top-1/2 -translate-y-1/2 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-md text-xs font-bold text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex items-center gap-1 shadow-sm" data-id="element-7134">
              <kbd className="font-mono" data-id="element-7135">{idx + 1}</kbd>
            </span>}
        </motion.button>)}
    </div>;
}